import {
  ContentLibraryOpenedFileEntry,
  ContentLibraryOpenedWhiteboard,
} from 'features/content-library/types';
import { ContentData, GridPanelName } from 'features/layout/features/content/types';
import { ProcessedFiles } from './processOpenedFiles';
import { ProcessedScreenshares } from './processScreenshares';
import { ProcessedWhiteboards } from './processOpenedWhiteboards';

export interface ActiveContent {
  activeTab: GridPanelName | null;
  openedFile: ContentLibraryOpenedFileEntry | null;
  openedWhiteboard: ContentLibraryOpenedWhiteboard | null;
  shouldLockContent: boolean;
}

export function determineActiveContent(
  screensharesResult: ProcessedScreenshares,
  filesResult: ProcessedFiles,
  whiteboardsResult: ProcessedWhiteboards,
  lockedContent: ContentData | null
): ActiveContent {
  let activeTab: GridPanelName | null = null;
  let openedFile = filesResult.latestItem;
  let openedWhiteboard = whiteboardsResult.latestItem;

  const candidates = [
    {
      timestamp: screensharesResult.latestTimestamp,
      hasContent: screensharesResult.hasLatestContent,
      tab: 'screenshare' as GridPanelName,
    },
    {
      timestamp: filesResult.latestTimestamp,
      hasContent: filesResult.hasLatestContent,
      tab: 'contentLibrary' as GridPanelName,
    },
    {
      timestamp: whiteboardsResult.latestTimestamp,
      hasContent: whiteboardsResult.hasLatestContent,
      tab: 'whiteboard' as GridPanelName,
    },
  ];

  const latestCandidate = candidates
    .filter((c) => c.hasContent)
    .reduce<(typeof candidates)[0] | null>((latest, current) => {
      if (!latest || current.timestamp > latest.timestamp) {
        return current;
      }
      return latest;
    }, null);

  if (latestCandidate) {
    activeTab = latestCandidate.tab;
  }

  const shouldLockContent =
    screensharesResult.hasLockedContent ||
    filesResult.hasLockedContent ||
    whiteboardsResult.hasLockedContent;

  if (lockedContent && shouldLockContent) {
    if (lockedContent.type === 'file') {
      activeTab = 'contentLibrary';
      openedFile = filesResult.lockedItem;
    } else if (lockedContent.type === 'whiteboard') {
      activeTab = 'whiteboard';
      openedWhiteboard = whiteboardsResult.lockedItem;
    } else if (lockedContent.type === 'screenshare') {
      activeTab = 'screenshare';
    }
  }

  return {
    activeTab,
    openedFile,
    openedWhiteboard,
    shouldLockContent,
  };
}
